import type { IamData, UserFilter } from 'features/iam/services/user-service';
import type { DateRange } from 'react-day-picker';
import type { TeamsMember } from './teams-mock-data';

function buildInitials(name: string, email: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length > 0) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase() || '??';
}

/** IAM often stores names in ALL CAPS; Figma People rows use title case. */
function toDisplayName(name: string) {
  if (!name || name !== name.toUpperCase()) return name;
  return name.toLowerCase().replace(/(^|[\s'-])[a-z]/g, (char) => char.toUpperCase());
}

function formatTeamsDate(value: string | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() === 1) return null;
  return date
    .toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .split('/')
    .join('.');
}

function resolvePlanName(user: IamData) {
  const planName = user.plan?.name?.trim();
  if (planName) return planName;
  const planItemTitle = user.planItem?.title?.trim();
  if (planItemTitle) return planItemTitle;
  return null;
}

export function mapIamUserToTeamsMember(user: IamData, currentUserId?: string): TeamsMember {
  const rawName =
    `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
  const name = toDisplayName(rawName);

  return {
    id: user.itemId,
    initials: buildInitials(name, user.email),
    name,
    email: user.email,
    isYou: currentUserId != null && user.itemId === currentUserId,
    plan: resolvePlanName(user),
    creditAccess: true,
    creditsUsed: null,
    lastLogin: formatTeamsDate(user.lastLoggedInTime),
    joinedOn: formatTeamsDate(user.createdDate),
    status: user.active ? 'active' : 'inactive',
    request: null,
  };
}

export type TeamsPeopleSearchMode = 'name' | 'email';

export type TeamsPeopleApiFilterInput = {
  query: string;
  searchMode: TeamsPeopleSearchMode;
  /** Status filter values: 'true' = active, 'false' = inactive. */
  statuses: string[];
  joinedOn?: DateRange;
  lastLogin?: DateRange;
};

/** Builds the IAM `getUsers` filter the same way the IAM admin table does. */
export function teamsPeopleApiFilter({
  query,
  searchMode,
  statuses,
  joinedOn,
  lastLogin,
}: TeamsPeopleApiFilterInput): UserFilter {
  const next: UserFilter = {};

  const trimmed = query.trim();
  if (trimmed) next[searchMode] = trimmed;

  // Both or none selected means no status filter.
  if (statuses.length === 1) {
    const isActive = statuses[0] === 'true';
    next.status = { active: isActive, inactive: !isActive };
  }

  // The API takes one date per field, so only the range start goes to the server.
  // The range end is filtered on the loaded page (see isOnOrBeforeRangeEnd).
  if (joinedOn?.from) next.joinedOn = joinedOn.from.toISOString();
  if (lastLogin?.from) next.lastLogin = lastLogin.from.toISOString();

  return next;
}

export function isOnOrBeforeRangeEnd(value: string | undefined, range?: DateRange) {
  if (!range?.to) return true;
  return new Date(value ?? '') <= range.to;
}
